import React from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import SearchIcon from '@material-ui/icons/Search';
import DeleteIcon from '@material-ui/icons/Delete';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';

interface VsphereNetwork {
  network: string;
  name: string;
  type: string;
}

export interface SelectedNetwork {
  networkName: string;
  replicas: number;
}

interface NetworkDisplayInfo {
  network: VsphereNetwork;
  vlan: string;
  poolName: string;
  typeLabel: string;
}

function parseVlan(networkName: string): string {
  const match = networkName.match(/(\d+)(?=[^\d]*$)/);
  return match ? match[1] : '?';
}

function generateYamlPreview(
  pools: SelectedNetwork[],
  networkInfos: NetworkDisplayInfo[],
): string {
  if (pools.length === 0) return '';
  let yaml = 'worker_pools:\n';
  for (const pool of pools) {
    const info = networkInfos.find(i => i.network.name === pool.networkName);
    const vlan = info?.vlan || '0';
    yaml += `  worker-vlan-${vlan}:\n`;
    yaml += `    replicas: ${pool.replicas}\n`;
    yaml += `    labels:\n`;
    yaml += `      vlan: "${vlan}"\n`;
    yaml += `      nestle.com/vlan: prd-vlan-${vlan}\n`;
    yaml += `    vmware_network_name: ${pool.networkName}\n`;
    yaml += '\n';
  }
  return yaml.trimEnd();
}

/**
 * Scaffolder field extension that fetches VMware vCenter networks
 * and lets the user select which ones to use as worker pools.
 *
 * Returns an array of { networkName, replicas } objects.
 * Used in templates with: ui:field: VmwareNetworkPicker
 */
export const VmwareNetworkPicker = ({
  onChange,
  rawErrors,
  formData,
  schema: { title = 'Worker Pool Networks' },
}: FieldExtensionComponentProps<SelectedNetwork[]>) => {
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const backendUrl = config.getString('backend.baseUrl');
  const api = `${backendUrl}/api/vmware-vsphere-api/networks`;

  const { value: networks, loading, error } = useAsync(
    async (): Promise<VsphereNetwork[]> => {
      const res = await fetchApi.fetch(api, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch networks: ${res.status} ${res.statusText}`);
      }
      return res.json();
    },
  );

  const [searchText, setSearchText] = React.useState('');

  // Normalize formData: filter out any old string-format entries
  const selectedPools: SelectedNetwork[] = React.useMemo(() => {
    if (!Array.isArray(formData)) return [];
    return formData.filter(
      (item): item is SelectedNetwork =>
        typeof item === 'object' &&
        item !== null &&
        'networkName' in item &&
        'replicas' in item,
    );
  }, [formData]);

  // Compute display info for each network (VLAN, pool name, type label)
  const networkDisplayInfos: NetworkDisplayInfo[] = React.useMemo(() => {
    if (!networks) return [];
    return networks.map(net => {
      const vlan = parseVlan(net.name);
      return {
        network: net,
        vlan,
        poolName: `worker-vlan-${vlan}`,
        typeLabel: (net.type || 'unknown').replace(/_/g, ' ').toLowerCase(),
      };
    });
  }, [networks]);

  // Filter networks based on search text
  const filteredNetworks = React.useMemo(() => {
    if (!searchText.trim()) return networkDisplayInfos;
    const lower = searchText.toLowerCase();
    return networkDisplayInfos.filter(
      info =>
        info.network.name.toLowerCase().includes(lower) ||
        info.vlan.includes(lower) ||
        info.poolName.toLowerCase().includes(lower),
    );
  }, [networkDisplayInfos, searchText]);

  // Generate YAML preview
  const yamlPreview = React.useMemo(
    () => generateYamlPreview(selectedPools, networkDisplayInfos),
    [selectedPools, networkDisplayInfos],
  );

  // Check for duplicate VLANs among selected
  const duplicateVlans = React.useMemo(() => {
    const vlans = selectedPools.map(p => {
      const info = networkDisplayInfos.find(i => i.network.name === p.networkName);
      return info?.vlan || '0';
    });
    return vlans.filter((v, i) => vlans.indexOf(v) !== i);
  }, [selectedPools, networkDisplayInfos]);

  const handleToggle = (networkName: string) => {
    const existing = selectedPools.find(p => p.networkName === networkName);
    if (existing) {
      onChange(selectedPools.filter(p => p.networkName !== networkName));
    } else {
      onChange([...selectedPools, { networkName, replicas: 1 }]);
    }
  };

  const handleReplicasChange = (networkName: string, replicas: number) => {
    onChange(
      selectedPools.map(p =>
        p.networkName === networkName ? { ...p, replicas } : p,
      ),
    );
  };

  const handleRemove = (networkName: string) => {
    onChange(selectedPools.filter(p => p.networkName !== networkName));
  };

  const isSelected = (networkName: string) =>
    selectedPools.some(p => p.networkName === networkName);

  if (loading) {
    return (
      <FormControl component="fieldset" fullWidth>
        <FormLabel>{title}</FormLabel>
        <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="textSecondary">
            Loading networks from vCenter...
          </Typography>
        </Box>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl component="fieldset" fullWidth error>
        <FormLabel>{title}</FormLabel>
        <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
          Could not load networks from vCenter: {error.message}
        </Typography>
        {rawErrors?.map((err, i) => (
          <FormHelperText key={i} error>{err}</FormHelperText>
        ))}
      </FormControl>
    );
  }

  return (
    <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend">{title}</FormLabel>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: 8 }}
        >
          {networks?.length ?? 0} networks available — select the ones for
          worker pools
        </Typography>

        {/* Section A: Search */}
        <TextField
          placeholder="Filter by network name, VLAN, or pool name..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ marginBottom: 8 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Section B: Available Networks */}
        <Paper
          variant="outlined"
          style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}
        >
          {filteredNetworks.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">
                No networks match your search
              </Typography>
            </Box>
          ) : (
            filteredNetworks.map(info => {
              const selected = isSelected(info.network.name);
              return (
                <Box
                  key={info.network.network}
                  display="flex"
                  alignItems="center"
                  px={1}
                  py={0.5}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    opacity: selected ? 0.6 : 1,
                    backgroundColor: selected
                      ? 'rgba(144, 202, 249, 0.08)'
                      : 'transparent',
                  }}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => handleToggle(info.network.name)}
                    size="small"
                    color="primary"
                  />
                  <Box flex={1} ml={0.5}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {info.network.name}
                      </Typography>
                      <Chip
                        label={`VLAN ${info.vlan}`}
                        size="small"
                        variant="outlined"
                        color={info.vlan === '?' ? 'default' : 'primary'}
                        style={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {info.typeLabel}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ fontFamily: 'monospace' }}
                    >
                      {info.poolName}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>

        {/* Section C: Selected Worker Pools */}
        {selectedPools.length > 0 && (
          <Box mb={2}>
            <Typography
              variant="subtitle2"
              style={{ marginBottom: 4 }}
            >
              Selected Worker Pools ({selectedPools.length})
            </Typography>
            {duplicateVlans.length > 0 && (
              <Typography
                variant="caption"
                color="error"
                style={{ display: 'block', marginBottom: 4 }}
              >
                Warning: Duplicate VLAN(s) detected: {duplicateVlans.join(', ')}. This may cause conflicts in the inventory file.
              </Typography>
            )}
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pool Name</TableCell>
                    <TableCell>Network</TableCell>
                    <TableCell>VLAN</TableCell>
                    <TableCell>Replicas</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPools.map(pool => {
                    const info = networkDisplayInfos.find(
                      i => i.network.name === pool.networkName,
                    );
                    return (
                      <TableRow key={pool.networkName}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{ fontFamily: 'monospace', fontWeight: 500 }}
                          >
                            {info?.poolName || `worker-vlan-?`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {pool.networkName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={info?.vlan || '?'}
                            size="small"
                            color="primary"
                            style={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={pool.replicas}
                            onChange={e =>
                              handleReplicasChange(
                                pool.networkName,
                                Number(e.target.value),
                              )
                            }
                            variant="outlined"
                            style={{ minWidth: 60, height: 32 }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(pool.networkName)}
                            title="Remove"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* Section D: Live YAML Preview */}
        {selectedPools.length > 0 && (
          <Box>
            <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
              Generated worker_pools preview
            </Typography>
            <Paper
              variant="outlined"
              style={{
                maxHeight: 400,
                overflowY: 'auto',
                padding: 12,
                backgroundColor: 'rgba(0,0,0,0.2)',
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontFamily: "'Roboto Mono', 'Courier New', monospace",
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {yamlPreview}
              </pre>
            </Paper>
          </Box>
        )}
      </FormControl>
  );
};
